import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Plus, Edit2 } from 'lucide-react';

const PRODUCT_TYPES = {
  honey: { label: 'Honey', icon: '🍯', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
  nuc: { label: 'Nuc', icon: '📦', bgColor: 'bg-violet-50', textColor: 'text-violet-700' },
  queen: { label: 'Queen', icon: '👑', bgColor: 'bg-pink-50', textColor: 'text-pink-700' },
  bee_package: { label: 'Bee Package', icon: '🐝', bgColor: 'bg-teal-50', textColor: 'text-teal-700' },
  hive: { label: 'Hive', icon: '🏠', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
  equipment: { label: 'Equipment', icon: '🔧', bgColor: 'bg-stone-50', textColor: 'text-stone-700' },
};

const SaleFormDialog = ({
  open, onOpenChange, editingSale, formData, setFormData,
  honeyInventory, harvestConfig, handleHoneyTypeChange, handleContainerChange,
  handleQuantityChange, yards, handleSubmit, saving,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {editingSale ? <Edit2 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          {editingSale ? 'Edit Sale' : 'Record Sale'}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        {/* Product Type Selection */}
        <div>
          <Label>Product Type *</Label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {Object.entries(PRODUCT_TYPES).map(([type, config]) => (
              <button
                key={type}
                onClick={() => {
                  if (type !== 'honey') {
                    setFormData({ ...formData, product_type: type, honey_type: '', container_type: '', quantity_kg: 0 });
                  } else {
                    setFormData({ ...formData, product_type: type });
                  }
                }}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.product_type === type
                    ? `${config.bgColor} border-current ${config.textColor}`
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <div className="text-2xl mb-1">{config.icon}</div>
                <div className="text-xs font-medium">{config.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Honey-Specific Fields */}
        {formData.product_type === 'honey' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-amber-700 font-medium">
              <span className="text-lg">🍯</span> Honey Sale Details
            </div>
            {honeyInventory.length > 0 && (
              <div className="bg-white rounded-md p-2 text-xs">
                <span className="text-stone-500">Available stock: </span>
                {honeyInventory.map((inv, idx) => (
                  <span key={inv.honey_type} className="text-amber-700">
                    {inv.honey_type}: {inv.total_kg?.toFixed(1) || 0}kg
                    {idx < honeyInventory.length - 1 && ', '}
                  </span>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-amber-800">Honey Type *</Label>
                <Select value={formData.honey_type} onValueChange={handleHoneyTypeChange}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Select honey type..." /></SelectTrigger>
                  <SelectContent>
                    {(harvestConfig?.honey_types || [])
                      .map(t => typeof t === 'string' ? { id: t, name: t, is_active: true } : t)
                      .filter(t => t.is_active !== false)
                      .map(type => (
                        <SelectItem key={type.id || type.name} value={type.name}>
                          {type.name} {type.price_per_unit > 0 && `($${type.price_per_unit}/kg)`}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-amber-800">Container Type</Label>
                <Select value={formData.container_type} onValueChange={handleContainerChange}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Select container..." /></SelectTrigger>
                  <SelectContent>
                    {(harvestConfig?.container_types || []).map(container => (
                      <SelectItem key={container.id || container.name} value={container.name}>
                        {container.name} ({container.capacity_kg}kg)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-amber-800">Qty (containers)</Label>
                <Input type="number" min="1" value={formData.quantity} onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-amber-800">Total kg</Label>
                <div className="mt-1 h-10 flex items-center px-3 bg-amber-100 rounded-md border border-amber-300">
                  <span className="font-medium text-amber-800">{formData.quantity_kg?.toFixed(1) || 0} kg</span>
                </div>
              </div>
              <div>
                <Label className="text-amber-800">Price/kg ($)</Label>
                <Input type="number" min="0" step="0.01" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" placeholder="Editable" />
              </div>
              <div>
                <Label className="text-amber-800">Total</Label>
                <div className="mt-1 h-10 flex items-center px-3 bg-green-100 rounded-md border border-green-300">
                  <span className="font-bold text-green-700">${((formData.quantity_kg || 0) * formData.unit_price).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Non-honey date + product name + qty/price */}
        {formData.product_type !== 'honey' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date *</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="mt-1" /></div>
              <div><Label>Product Name</Label><Input value={formData.product_name} onChange={(e) => setFormData({ ...formData, product_name: e.target.value })} placeholder={`e.g., "5-Frame Nuc"`} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Quantity</Label><Input type="number" min="1" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })} className="mt-1" /></div>
              <div><Label>Unit Price ($)</Label><Input type="number" min="0" step="0.01" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })} className="mt-1" /></div>
              <div><Label>Total</Label><div className="mt-1 h-10 flex items-center px-3 bg-green-50 rounded-md border border-green-200"><span className="font-bold text-green-700">${(formData.quantity * formData.unit_price).toFixed(2)}</span></div></div>
            </div>
          </>
        )}

        {/* Honey date */}
        {formData.product_type === 'honey' && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date *</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="mt-1" /></div>
            <div><Label>Product Name</Label><Input value={formData.product_name} onChange={(e) => setFormData({ ...formData, product_name: e.target.value })} placeholder="Auto-filled from honey type" className="mt-1" /></div>
          </div>
        )}

        {/* Customer + Yard + Payment */}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Customer Name</Label><Input value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} placeholder="Optional" className="mt-1" /></div>
          <div><Label>Contact Info</Label><Input value={formData.customer_contact} onChange={(e) => setFormData({ ...formData, customer_contact: e.target.value })} placeholder="Phone or email" className="mt-1" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>From Apiary</Label>
            <Select value={formData.yard || 'none'} onValueChange={(v) => setFormData({ ...formData, yard: v === 'none' ? '' : v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select apiary..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                {yards.map(yard => (<SelectItem key={yard.id || yard.name} value={yard.name}>{yard.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Payment Method</Label>
            <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Payment Status</Label>
          <div className="flex gap-2 mt-2">
            {['paid', 'pending', 'partial'].map(status => (
              <button key={status} onClick={() => setFormData({ ...formData, payment_status: status })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  formData.payment_status === status
                    ? status === 'paid' ? 'bg-green-100 text-green-700 border-2 border-green-300'
                      : status === 'pending' ? 'bg-amber-100 text-amber-700 border-2 border-amber-300'
                      : 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                    : 'bg-stone-100 text-stone-600 border-2 border-transparent'
                }`}
              >{status.charAt(0).toUpperCase() + status.slice(1)}</button>
            ))}
          </div>
        </div>
        <div><Label>Notes</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes..." className="mt-1" rows={2} /></div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? 'Saving...' : editingSale ? 'Update Sale' : 'Record Sale'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default SaleFormDialog;
